import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getDossier } from "../../api/dossier/getDossier";
import { terminateDossier } from "../../api/dossier/terminateDossier";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import InfoButton from "../../components/buttons/InfoButton";
import FaqList from "../../components/faq/FaqList";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../components/form/APAutoComplete";
import APCheckbox from "../../components/form/APCheckbox";
import APDateField from "../../components/form/APDateField";
import APForm from "../../components/form/APForm";
import APRadioGroup from "../../components/form/APRadioGroup";
import APTextField from "../../components/form/APTextField";
import PageContainer from "../../components/PageContainer";
import DossierSummary from "../../employer/components/DossierSummary";
import DossierTerminationSummary from "../../employer/components/DossierTerminationSummary";
import { dateFromString, isAfter } from "../../helpers/dates";
import { useFaqs } from "../../hooks/useFaqs";
import { useAuth } from "../../providers/AuthProvider";
import {
  DossierTerminationReason,
  DossierTerminationReasonLabelMap,
} from "../../types/Dossier";

type TerminateDossierInputs = {
  leavingEmployer: "yes" | "no" | null;
  terminationReason: AutoCompleteOption | null;
  lastDateOfEmployment: string | null;
  endDate: string | null;
  noteFromDriver: string;
  confirm: boolean;
};

const leavingEmployerRadioOptions: {
  id: string;
  label: string;
}[] = [
  { id: "yes", label: "Yes" },
  { id: "no", label: "No" },
];

type TerminationReasonOption = AutoCompleteOption & {
  leavingEmployer: boolean;
};

const terminationReasonOptions: TerminationReasonOption[] = [
  {
    id: "moving-to-a-new-employer",
    label: DossierTerminationReasonLabelMap["moving-to-a-new-employer"],
    leavingEmployer: true,
  },
  {
    id: "resigned",
    label: DossierTerminationReasonLabelMap["resigned"],
    leavingEmployer: true,
  },
  {
    id: "redundancy-or-employer-termination",
    label:
      DossierTerminationReasonLabelMap["redundancy-or-employer-termination"],
    leavingEmployer: true,
  },
  {
    id: "replacing-vehicle",
    label: DossierTerminationReasonLabelMap["replacing-vehicle"],
    leavingEmployer: false,
  },
  {
    id: "selling-vehicle",
    label: DossierTerminationReasonLabelMap["selling-vehicle"],
    leavingEmployer: false,
  },
  {
    id: "keeping-vehicle",
    label: DossierTerminationReasonLabelMap["keeping-vehicle"],
    leavingEmployer: false,
  },
];

const TerminateDossier = () => {
  const { dossierId } = useParams();
  const { getScopedPageUrl } = useAuth();
  const [isConfirming, setIsConfirming] = useState(false);
  const navigate = useNavigate();
  const [navigationCheckComplete, setNavigationCheck] = useState(false);
  const [searchParams] = useSearchParams();
  const prevLeavingEmployerRef = useRef<string | null>(null);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<TerminateDossierInputs>({
    mode: "onTouched",
    defaultValues: {
      leavingEmployer: null,
      terminationReason: null,
      lastDateOfEmployment: null,
      endDate: null,
    },
  });

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const dossier = getDossier(dossierId);

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "driver-request-lease-closure",
    contextIds: {
      opportunityId: dossier?.data?.opportunity?.id,
    },
  });

  if (dossier.isError) {
    throw dossier.error;
  }

  const { mutateAsync, isLoading: isSubmitting } = terminateDossier(dossierId, {
    mutationOptions: {
      successNavPath: getScopedPageUrl("dossierViewTermination", dossierId),
      successReplaceHistory: true,
      successAlertMessage: "Termination commenced.",
    },
  });

  const leavingEmployer = watch("leavingEmployer");
  const terminationReason = watch("terminationReason");
  const lastDateOfEmployment = watch("lastDateOfEmployment");
  const endDate = watch("endDate");

  const _filterTerminationReasonOptions = (
    leavingEmployer: "yes" | "no" | null
  ) => {
    return terminationReasonOptions.filter(
      (option) =>
        option.leavingEmployer === (leavingEmployer === "yes" ? true : false)
    );
  };

  const filteredTerminationReasonOptions = useMemo(
    () => _filterTerminationReasonOptions(leavingEmployer),
    [leavingEmployer]
  );

  useEffect(() => {
    if (dossier.isSuccess) {
      if (dossier.data.status === "Active") {
        setNavigationCheck(true);
      } else if (
        dossier.data.terminationId &&
        dossier.data.closureStatus === "aware"
      ) {
        navigate(getScopedPageUrl("dossierViewTermination", dossierId), {
          replace: true,
        });
      } else {
        navigate(getScopedPageUrl("dossierActivated", dossierId), {
          replace: true,
        });
      }
    }
  }, [dossier.isSuccess]);

  useEffect(() => {
    const isLeavingEmployerParam = searchParams.get("isLeavingEmployer");
    const terminationReasonParam = searchParams.get("terminationReason");
    const isLeavingEmployer =
      isLeavingEmployerParam === "true"
        ? "yes"
        : isLeavingEmployerParam === "false"
        ? "no"
        : null;

    if (isLeavingEmployer !== null) {
      setValue("leavingEmployer", isLeavingEmployer);

      if (terminationReasonParam) {
        const validReasons = _filterTerminationReasonOptions(isLeavingEmployer);
        const selectedReason = validReasons.find(
          (option) => option.id === terminationReasonParam
        );

        if (selectedReason) {
          setValue("terminationReason", selectedReason);
        }
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const prevValue = prevLeavingEmployerRef.current;

    // 2. Update the ref immediately with the current value for the next run
    prevLeavingEmployerRef.current = leavingEmployer;

    // 3. Skip clearing if it's the initial value transition (null -> "yes"/"no")
    if (prevValue === null) {
      return;
    }

    // 4. Skip clearing if the value didn't actually change (Strict Mode double-runs)
    if (prevValue === leavingEmployer) {
      return;
    }

    setValue("terminationReason", null);
    setValue("lastDateOfEmployment", null);
    setValue("endDate", null);
  }, [leavingEmployer]);

  useEffect(() => {
    if (!terminationReason) {
      setValue("endDate", null);
    }
  }, [terminationReason]);

  useEffect(() => {
    if (
      lastDateOfEmployment &&
      endDate &&
      isAfter(endDate, lastDateOfEmployment)
    ) {
      setValue("endDate", null);
    }
  }, [lastDateOfEmployment, endDate]);

  const onSubmit: SubmitHandler<TerminateDossierInputs> = async (data) => {
    if (!data.terminationReason || !data.endDate) {
      return;
    }

    if (!isConfirming) {
      setIsConfirming(true);
      window.scrollTo(0, 0);
      return;
    }

    await mutateAsync({
      leavingEmployer: data.leavingEmployer === "yes" ? true : false,
      lastDateOfEmployment: data.lastDateOfEmployment ?? undefined,
      endDate: data.endDate,
      terminationReason: data.terminationReason.id as DossierTerminationReason,
      noteFromDriver: data.noteFromDriver ?? null,
    });
  };

  const onConfirmBack = () => {
    setIsConfirming(false);
    setValue("confirm", false);
    window.scrollTo(0, 0);
  };

  const terminationSummary = useMemo(() => {
    if (isConfirming) {
      const values = getValues();

      return (
        <DossierTerminationSummary
          dossierTermination={{
            leavingEmployer: values.leavingEmployer === "yes" ? true : false,
            lastDateOfEmployment: values.lastDateOfEmployment,
            endDate: values.endDate,
            terminationReason: values.terminationReason
              ?.id as DossierTerminationReason,
            noteFromDriver: values.noteFromDriver ?? null,
          }}
          displayedFields={[
            "leavingEmployer",
            "lastDateOfEmployment",
            "terminationReason",
            "endDate",
            "noteFromDriver",
          ]}
        />
      );
    }

    return <></>;
  }, [isConfirming]);

  const isLoading =
    dossier.isLoading ||
    isSubmitting ||
    !navigationCheckComplete ||
    isLoadingFaqs;
  const isSuccess =
    dossier.isSuccess && navigationCheckComplete && isSuccessFaqs;

  const displayLastDateOfEmployment = leavingEmployer === "yes";
  const lastDateOfEmploymentCheck =
    leavingEmployer === "no" ||
    (leavingEmployer === "yes" && lastDateOfEmployment !== null);
  const displayTerminationReason =
    lastDateOfEmploymentCheck || !!terminationReason;
  const displayEndDate =
    terminationReason !== null && lastDateOfEmploymentCheck;

  const renderAppLocation = faqProps.renderAppLocation;

  return (
    <PageContainer
      title={isConfirming ? "Confirm Termination" : "Request Termination"}
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {renderAppLocation(
              "DriverRequestClose-Page",
              <InfoButton />,
              true,
              true
            )}
          </Box>
        ) : undefined
      }
      loading={isLoading}
    >
      {isSuccess && dossier.data && (
        <Stack spacing={2}>
          <Typography variant="titleMedium">
            {isConfirming
              ? "Please confirm that you wish to initiate the termination of the following novated lease with your employer."
              : "Please complete the following to initiate termination with your employer."}
          </Typography>

          <DossierSummary
            dossier={dossier.data}
            displayedFields={["dossierIdentity", "startDate", "vehicle"]}
          />

          <APForm
            onSubmit={handleSubmit(onSubmit)}
            submitText={isConfirming ? "Submit" : "Next"}
            submitIcon={<PlayArrowOutlined />}
            leftButton={
              <Stack
                spacing={2}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <BackButton
                  onClick={isConfirming ? onConfirmBack : undefined}
                />
                <APContactHelpButton
                  regardingOpportunityId={
                    dossier.data.opportunity?.id ?? undefined
                  }
                />
              </Stack>
            }
            isLoading={isSubmitting}
            isError={Object.keys(errors).length > 0}
          >
            {isConfirming && terminationSummary}

            {!isConfirming && (
              <Box>
                <APRadioGroup
                  name="leavingEmployer"
                  label="Are you leaving your employer?"
                  validations={{ required: true }}
                  errors={errors}
                  control={control}
                  defaultValue={null}
                  options={leavingEmployerRadioOptions}
                  hidden={isConfirming}
                />
              </Box>
            )}

            {displayLastDateOfEmployment && (
              <APDateField
                name="lastDateOfEmployment"
                label="Last Date of Employment"
                control={control}
                defaultValue={null}
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
                validations={{ required: true }}
                dateOnlyFormat={true}
                hidden={isConfirming}
              />
            )}

            {displayTerminationReason && (
              <APAutoComplete
                name="terminationReason"
                label="Termination Reason"
                options={filteredTerminationReasonOptions}
                control={control}
                validations={{ required: true }}
                errors={errors}
                defaultValue={null}
                setValue={setValue}
                hidden={isConfirming}
              />
            )}

            {displayEndDate && (
              <APDateField
                name="endDate"
                label="Requested End Date"
                control={control}
                defaultValue={null}
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
                validations={{ required: true }}
                dateOnlyFormat={true}
                maxDate={
                  leavingEmployer && lastDateOfEmployment
                    ? dateFromString(lastDateOfEmployment)
                    : undefined
                }
                maxDateErrorMessage="The end date cannot be after the last date of employment"
                hidden={isConfirming}
              />
            )}

            <APTextField<TerminateDossierInputs>
              name="noteFromDriver"
              label="Note"
              control={control}
              validations={{}}
              errors={errors}
              defaultValue=""
              multiline
              minRows={1}
              hidden={isConfirming}
            />

            {isConfirming && (
              <APCheckbox
                name="confirm"
                control={control}
                label="Please initiate the termination of this novated lease. This will notify your employer."
                defaultValue={false}
                validations={{ required: true }}
                errors={errors}
                hideRequired
              />
            )}

            {!isConfirming && (
              <FaqList
                pageId="driver-request-lease-closure"
                appLocationIdentifier="DriverRequestClose-FAQList"
                faqContextIds={{
                  opportunityId: dossier.data.opportunity?.id ?? undefined,
                }}
              />
            )}
          </APForm>
        </Stack>
      )}
    </PageContainer>
  );
};

export default TerminateDossier;
